import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { usePortalBranding } from '@/hooks/usePortalBranding';
import { Upload, X, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { WebsiteAnalyzer } from './WebsiteAnalyzer';
import { Separator } from '@/components/ui/separator';

export function ContractorPortalBranding() {
  const { branding, isLoading, uploadLogo, updateColors, deleteLogo, applyAutoBranding } = usePortalBranding();
  const [primaryColor, setPrimaryColor] = useState(branding?.portal_primary_color || '#3b82f6');
  const [secondaryColor, setSecondaryColor] = useState(branding?.portal_secondary_color || '#8b5cf6');
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    try {
      await uploadLogo(file);
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const handleColorsSave = async () => {
    await updateColors({
      primary: primaryColor,
      secondary: secondaryColor,
    });
    setHasChanges(false);
  };

  const handleReset = () => {
    setPrimaryColor('#3b82f6');
    setSecondaryColor('#8b5cf6');
    setHasChanges(true);
  };

  const handleApplyBranding = async (analysis: any) => {
    await applyAutoBranding({
      logo_url: analysis.logo_url,
      primary_color: analysis.primary_color,
      secondary_color: analysis.secondary_color,
      source_website: analysis.source_website || '',
    });

    // Update local state to reflect new colors
    if (analysis.primary_color) {
      setPrimaryColor(`hsl(${analysis.primary_color})`);
    }
    if (analysis.secondary_color) {
      setSecondaryColor(`hsl(${analysis.secondary_color})`);
    }
  };

  if (isLoading) {
    return <Card><CardContent className="py-6">Loading...</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contractor Portal Branding</CardTitle>
        <CardDescription>
          Customize the look and feel of the contractor portal
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <WebsiteAnalyzer onApplyBranding={handleApplyBranding} />
        
        <Separator className="my-6" />
        
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Manual Configuration</h3>
          <div>
            <Label>Portal Logo</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Recommended size: 200x60px. Max file size: 2MB
            </p>
            {branding?.portal_logo_url ? (
              <div className="flex items-center gap-4">
                <div className="border rounded-lg p-4 bg-white">
                  <img
                    src={branding.portal_logo_url}
                    alt="Portal Logo"
                    className="max-h-16 max-w-[200px] object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Replace
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteLogo()}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Logo
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="primaryColor">Primary Color</Label>
            <div className="flex gap-2">
              <Input
                id="primaryColor"
                type="color"
                value={primaryColor}
                onChange={(e) => {
                  setPrimaryColor(e.target.value);
                  setHasChanges(true);
                }}
                className="w-20 h-10 cursor-pointer"
              />
              <Input
                type="text"
                value={primaryColor}
                onChange={(e) => {
                  setPrimaryColor(e.target.value);
                  setHasChanges(true);
                }}
                placeholder="#3b82f6"
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Used for buttons, headers, and primary UI elements
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="secondaryColor">Secondary Color</Label>
            <div className="flex gap-2">
              <Input
                id="secondaryColor"
                type="color"
                value={secondaryColor}
                onChange={(e) => {
                  setSecondaryColor(e.target.value);
                  setHasChanges(true);
                }}
                className="w-20 h-10 cursor-pointer"
              />
              <Input
                type="text"
                value={secondaryColor}
                onChange={(e) => {
                  setSecondaryColor(e.target.value);
                  setHasChanges(true);
                }}
                placeholder="#8b5cf6"
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Used for accents, links, and secondary UI elements
            </p>
          </div>
        </div>

        <div className="rounded-lg border p-4 space-y-3" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="text-sm font-medium">Preview</div>
          <div className="space-y-2">
            <Button style={{ backgroundColor: primaryColor, borderColor: primaryColor }}>
              Primary Button
            </Button>
            <Button variant="outline" style={{ color: secondaryColor, borderColor: secondaryColor }}>
              Secondary Button
            </Button>
            <div>
              <a href="#" style={{ color: secondaryColor }} className="text-sm underline">
                Sample Link
              </a>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleColorsSave} disabled={!hasChanges}>
            Save Branding
          </Button>
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
